import { img } from "@/lib/media";

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d.getTime()) ? String(s).slice(0, 10) : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); };
const FONT_STACKS = {
  "Inter": "'Inter', sans-serif",
  "Roboto": "'Roboto', sans-serif",
  "Poppins": "'Poppins', sans-serif",
  "Open Sans": "'Open Sans', sans-serif",
  "Lato": "'Lato', sans-serif",
  "Montserrat": "'Montserrat', sans-serif",
  "Arial": "Arial, Helvetica, sans-serif",
  "Georgia": "Georgia, 'Times New Roman', serif",
};

// ---- helpers to derive readable header text colors from the chosen header color ----
const hexToRgb = (h) => { let s = (h || "").replace("#", ""); if (s.length === 3) s = s.split("").map((c) => c + c).join(""); const n = parseInt(s || "ffffff", 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
const toHex = (r, g, b) => `#${[r, g, b].map((x) => clamp(x).toString(16).padStart(2, "0")).join("")}`;
const mix = (h1, h2, amt) => { const a = hexToRgb(h1), b = hexToRgb(h2); return toHex(a[0] + (b[0] - a[0]) * amt, a[1] + (b[1] - a[1]) * amt, a[2] + (b[2] - a[2]) * amt); };
const relLum = (h) => { const [r, g, b] = hexToRgb(h).map((c) => c / 255); const f = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)); return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b); };

// Professional printable invoice. The chosen background color themes ONLY the top header band;
// the rest of the document (Bill To, items, totals, payment, notes, footer) stays clean/white.
export default function InvoiceDocument({
  store = {}, number, createdAt, serviceDate, dueDate, client = {}, items = [],
  subtotal = 0, discount = 0, shipping = 0, taxLines = [], taxAmount = 0, total = 0,
  amountPaid = 0, balance = 0, paymentMethod, paymentStatus, tag, notes, terms, testidPrefix = "doc",
  bgColor = "#ffffff", fontFamily = "Inter", textColor = "#1f2d3d",
}) {
  const logo = store.logo || store.logo_url || "";
  const hasClient = client.name || client.email || client.phone || client.address;
  const taxable = taxLines.length > 0 || taxAmount > 0;

  // Header-only theme
  const hdrBg = (bgColor || "#ffffff").trim() || "#ffffff";
  const hdrLight = relLum(hdrBg) > 0.5;
  const hdrInk = hdrLight ? "#12283d" : "#ffffff";
  const hdrSub = hdrLight ? "#3f5468" : "rgba(255,255,255,0.85)";
  const hdrTitle = hdrLight ? "#0b2740" : "#ffffff";
  const hdrGrad = hdrLight ? mix(hdrBg, "#ffffff", 0.14) : mix(hdrBg, "#000000", 0.14);
  const hdrBorder = hdrLight ? mix(hdrBg, "#0f172a", 0.12) : mix(hdrBg, "#ffffff", 0.18);

  // Body neutrals — text ink follows the saved theme; secondary stays a readable neutral
  const INK = (textColor && String(textColor).trim()) || "#1f2d3d";
  const SUB = "#64748b", BORDER = "#e5e7eb", BAND = "#f1f5f9", SOFT = "#f8fafc";
  const statusColor = /unpaid|overdue|partial/i.test(paymentStatus || "") ? "#d97706" : (/paid/i.test(paymentStatus || "") ? "#16a34a" : INK);
  const fontStack = FONT_STACKS[fontFamily] || FONT_STACKS.Inter;

  return (
    <div data-testid={`${testidPrefix}-invoice-doc`} className="mx-auto w-full overflow-hidden rounded-lg" style={{ maxWidth: 820, background: "#ffffff", color: INK, fontFamily: fontStack, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", outline: `1px solid ${BORDER}` }}>
      {/* Header band: enlarged logo (left) · centered "Invoice" title · business info (right) — the ONLY tinted section */}
      <div data-testid={`${testidPrefix}-invoice-header`} className="px-8 py-7" style={{ background: `linear-gradient(135deg, ${hdrBg}, ${hdrGrad})`, borderBottom: `1px solid ${hdrBorder}`, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
        <div className="grid grid-cols-3 items-center gap-4">
          <div className="flex items-center">
            <div className="h-36 w-36 rounded-xl flex items-center justify-center overflow-hidden shrink-0 p-2" style={{ background: "#ffffff", boxShadow: "0 1px 6px rgba(0,0,0,0.18)", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
              {logo
                ? <img src={img(logo)} alt="logo" className="h-full w-full object-contain" />
                : <span className="text-5xl font-extrabold" style={{ color: hdrTitle }}>{(store.name || "•").charAt(0)}</span>}
            </div>
          </div>
          <div className="text-center">
            <p className="text-5xl font-semibold leading-none tracking-tight" style={{ color: hdrTitle, opacity: 0.95 }}>Invoice</p>
          </div>
          <div className="text-right min-w-0">
            <p className="text-lg font-extrabold" style={{ color: hdrInk }}>{store.name || "Company Name"}</p>
            <div className="text-[12px] mt-1 leading-relaxed" style={{ color: hdrSub }}>
              {store.phone && <div>{store.phone}</div>}
              {store.email && <div className="break-all">{store.email}</div>}
              {store.website && <div className="break-all">{store.website}</div>}
              {store.tax_number && <div>HST# {store.tax_number}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Bill To (left) + invoice meta (right) */}
      <div className="px-8 pt-6 flex justify-between gap-8">
        <div className="min-w-0">
          <p className="text-[13px] mb-1" style={{ color: SUB }}>Bill To:</p>
          {hasClient ? (
            <div className="text-[13px] leading-relaxed">
              {client.name && <p className="font-bold" style={{ color: INK }}>{client.name}</p>}
              {client.sub && <p style={{ color: INK }}>{client.sub}</p>}
              {client.email && <p style={{ color: SUB }}>{client.email}</p>}
              {client.address && <p className="whitespace-pre-line" style={{ color: SUB }}>{client.address}</p>}
              {client.phone && <p style={{ color: SUB }}>{client.phone}</p>}
            </div>
          ) : <p className="text-sm italic" style={{ color: SUB }}>No client selected</p>}
        </div>
        <div className="text-right shrink-0 min-w-[240px] text-[13px]">
          <p className="text-base font-extrabold mb-2" style={{ color: INK }}>#{number || "—"}</p>
          <MetaRow label="Issue on:" value={fmtDate(createdAt)} sub={SUB} ink={INK} />
          <MetaRow label="Due:" value={dueDate ? fmtDate(dueDate) : "Upon receipt"} sub={SUB} ink={INK} />
          <MetaRow label="Status:" value={paymentStatus || "—"} sub={SUB} ink={INK} valueColor={statusColor} strong />
          <MetaRow label="Balance:" value={fmt(balance)} sub={SUB} ink={INK} strong />
        </div>
      </div>

      {/* Items */}
      <div className="px-8 pt-6">
        <p className="text-[13px] font-bold mb-2" style={{ color: INK }}>{items.length} × Item{items.length === 1 ? "" : "s"}</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-[12px] border-collapse">
            <thead>
              <tr className="text-left" style={{ background: hdrBg, color: hdrInk, borderTop: `1px solid ${hdrBorder}`, borderBottom: `1px solid ${hdrBorder}`, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                <th className="py-2.5 px-3 font-semibold">Product</th>
                <th className="py-2.5 px-3 font-semibold">Description</th>
                <th className="py-2.5 px-3 font-semibold text-center">QTY</th>
                <th className="py-2.5 px-3 font-semibold text-right">Price</th>
                <th className="py-2.5 px-3 font-semibold text-right">Total</th>
              </tr>
            </thead>
            <tbody data-testid={`${testidPrefix}-preview-items`}>
              {items.map((it, i) => (
                <tr key={i} className="align-top" style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <td className="py-3 px-3">
                    {taxable && <div className="text-[10px] italic mb-0.5" style={{ color: SUB }}>Tax</div>}
                    <span className="font-semibold" style={{ color: INK }}>{it.name}{it.uom ? <span className="font-normal" style={{ color: SUB }}> · {it.uom}</span> : ""}</span>
                    {it.part_number && <div className="text-[11px] font-mono mt-0.5" style={{ color: SUB }}>{it.part_number}</div>}
                  </td>
                  <td className="py-3 px-3" style={{ color: SUB }}>{it.description || "—"}</td>
                  <td className="py-3 px-3 text-center" style={{ color: INK }}>{it.quantity}</td>
                  <td className="py-3 px-3 text-right whitespace-nowrap" style={{ color: INK }}>{fmt(it.price)}</td>
                  <td className="py-3 px-3 text-right font-semibold whitespace-nowrap" style={{ color: INK }}>{fmt(it.total)}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="py-10 text-center italic" style={{ color: SUB }}>No items added yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div className="px-8 py-8 flex justify-end">
        <div className="w-full max-w-[320px] text-sm">
          <TotRow label="Subtotal" value={fmt(subtotal)} sub={SUB} ink={INK} testid={`${testidPrefix}-preview-subtotal`} />
          {discount > 0 && <TotRow label="Discount" value={`−${fmt(discount)}`} sub={SUB} ink={INK} />}
          {shipping > 0 && <TotRow label="Shipping" value={fmt(shipping)} sub={SUB} ink={INK} />}
          {taxLines.length
            ? taxLines.map((t, i) => <TotRow key={i} label={t.rate != null ? `${t.name} (${t.rate}%)` : t.name} value={fmt(t.amount)} sub={SUB} ink={INK} />)
            : <TotRow label="Tax" value={fmt(taxAmount)} sub={SUB} ink={INK} />}
          <div className="flex justify-between items-center px-3 py-2.5 mt-2" style={{ background: hdrBg, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
            <span className="font-bold" style={{ color: hdrInk }}>Total</span>
            <span data-testid={`${testidPrefix}-preview-total`} className="font-extrabold text-base" style={{ color: hdrInk }}>{fmt(total)}</span>
          </div>
          {amountPaid > 0 && <TotRow label="Amount paid" value={fmt(amountPaid)} sub={SUB} ink={INK} />}
          <div className="flex justify-between px-3 py-2 mt-1 font-bold" style={{ color: INK }}>
            <span>Balance Due</span>
            <span data-testid={`${testidPrefix}-preview-balance`}>{fmt(balance)}</span>
          </div>
        </div>
      </div>

      {(paymentMethod || paymentStatus) && (
        <div className="px-8 pb-5">
          <div className="rounded-lg px-4 py-3 flex items-center justify-between text-sm" style={{ background: SOFT, border: `1px solid ${BORDER}` }}>
            <span style={{ color: SUB }}>Payment method: <b style={{ color: INK }}>{paymentMethod || "—"}</b></span>
            {paymentStatus && <span className="px-2.5 py-1 rounded-full text-xs font-semibold capitalize" style={{ background: "#ffffff", color: INK, border: `1px solid ${BORDER}` }}>{paymentStatus}</span>}
          </div>
        </div>
      )}

      {notes && <div className="px-8 pb-5"><p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: SUB }}>Notes</p><p className="text-sm whitespace-pre-wrap" style={{ color: SUB }}>{notes}</p></div>}

      {terms && <div className="px-8 pb-5"><p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: SUB }}>Terms</p><p className="text-sm whitespace-pre-wrap" style={{ color: SUB }}>{terms}</p></div>}

      <div className="px-8 py-4 text-center text-[11px]" style={{ borderTop: `1px solid ${BORDER}`, color: SUB }}>
        Thank you for your business{store.website ? ` · ${store.website}` : ""}
      </div>
    </div>
  );
}

function MetaRow({ label, value, strong, valueColor, sub, ink }) {
  return <div className="flex justify-between gap-6 py-0.5"><span style={{ color: sub }}>{label}</span><span className={`${strong ? "font-bold" : "font-medium"} capitalize`} style={{ color: valueColor || ink }}>{value}</span></div>;
}
function TotRow({ label, value, sub, ink, testid }) {
  return <div className="flex justify-between py-1"><span style={{ color: sub }}>{label}</span><span data-testid={testid} className="font-medium" style={{ color: ink }}>{value}</span></div>;
}
